import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import HelpLayout from "../../components/HelpLayout";
import {
  Diagram30360,
  DiagramCapBypassChain,
  DiagramWorkflow,
  DiagramCapBreakdown,
} from "./HelpDiagrams";

/**
 * HelpPage — Vollständige Doku-Seite für /hilfe.
 *
 * Iter 19.63.108 — User-Wunsch: vollwertige Dokumentation im Stripe-/
 * Linear-Docs-Stil. Zweispaltig auf Desktop (Sticky-TOC + Content),
 * Akkordeon-TOC auf Mobile. Scroll-Spy via IntersectionObserver markiert
 * den aktuellen TOC-Eintrag.
 *
 * WICHTIG: Texte sprechen NICHT von "KI" sondern ausschließlich von
 * "ET" als System (User-Anforderung). Alles aus User-Sicht beschrieben,
 * keine internen Iter-Refs oder Code-Begriffe.
 */

/** Sektions-Metadaten — Reihenfolge bestimmt TOC und Page-Order. */
const SECTIONS = [
  { id: "willkommen", title: "Willkommen", subsections: [] },
  {
    id: "workflow",
    title: "Der 4-Schritt-Workflow",
    subsections: [
      { id: "schritt-1", title: "Schritt 1 — KV & Stichtag" },
      { id: "schritt-2", title: "Schritt 2 — Dokumente hochladen" },
      { id: "schritt-3", title: "Schritt 3 — Vordienst prüfen" },
      { id: "schritt-4", title: "Schritt 4 — Empfehlung & PDF" },
    ],
  },
  {
    id: "math",
    title: "Berechnung & Regeln verständlich erklärt",
    subsections: [
      { id: "math-30360", title: "Die 30/360-Rechenmethode" },
      { id: "math-cap", title: "Höchstanrechnungsdauer" },
      { id: "math-bypass", title: 'Sonderregel „Selber Arbeitgeber“' },
      { id: "math-overlap", title: "Überlappungen" },
      { id: "math-faktor", title: "Anrechnungsfaktor" },
    ],
  },
  { id: "transparenz", title: "Transparenz & Determinismus", subsections: [] },
  { id: "datenschutz", title: "Datenschutz", subsections: [] },
  { id: "faq", title: "Häufige Fragen (FAQ)", subsections: [] },
  { id: "glossar", title: "Glossar", subsections: [] },
  { id: "support", title: "Support & Feedback", subsections: [] },
];

/** Flat-Liste aller scroll-spy-Targets (Haupt + Subsections). */
function allAnchors() {
  const flat = [];
  for (const s of SECTIONS) {
    flat.push(s.id);
    for (const sub of s.subsections) flat.push(sub.id);
  }
  return flat;
}

export default function HelpPage() {
  const location = useLocation();
  const [activeId, setActiveId] = useState("willkommen");
  const observerRef = useRef(null);

  // Scroll-Spy: IntersectionObserver markiert aktive Sektion.
  useEffect(() => {
    const anchors = allAnchors();
    const els = anchors
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (els.length === 0) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        // Wähle den Eintrag mit dem höchsten intersection-ratio, der gerade
        // sichtbar ist. Bei mehreren: bevorzuge den, der höher im Viewport
        // steht (= „aktueller Fokus“).
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: "-15% 0px -70% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    els.forEach((el) => obs.observe(el));
    observerRef.current = obs;
    return () => obs.disconnect();
  }, []);

  // Deep-Link-Support: wenn URL einen #hash hat, dorthin scrollen.
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace(/^#/, "");
      const el = document.getElementById(id);
      if (el) {
        // Kleiner Delay, damit Sticky-Header die scroll-position richtig korrigiert.
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
      }
    }
  }, [location.hash]);

  const docTitle = useMemo(() => "ET · Dokumentation", []);
  useEffect(() => {
    const old = document.title;
    document.title = docTitle;
    return () => {
      document.title = old;
    };
  }, [docTitle]);

  return (
    <HelpLayout>
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10 grid gap-10 lg:grid-cols-[260px_minmax(0,1fr)]">
        {/* Sticky TOC */}
        <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto print:hidden">
          <details
            className="lg:open:!block group"
            open
            data-testid="help-toc"
          >
            <summary className="lg:hidden cursor-pointer text-xs font-bold uppercase tracking-wider text-warm-700 mb-3 select-none">
              Inhaltsverzeichnis
              <span className="ml-2 text-warm-500 group-open:hidden">↓ aufklappen</span>
              <span className="ml-2 text-warm-500 hidden group-open:inline">↑ schließen</span>
            </summary>
            <div className="hidden lg:block text-[10px] font-bold uppercase tracking-wider text-warm-500 mb-3">
              Inhaltsverzeichnis
            </div>
            <nav className="space-y-1 text-sm">
              {SECTIONS.map((s, i) => (
                <div key={s.id}>
                  <a
                    href={`#${s.id}`}
                    data-testid={`help-toc-link-${s.id}`}
                    className={[
                      "block py-1 px-2 rounded-sm transition-crisp",
                      activeId === s.id ||
                      s.subsections.some((sub) => sub.id === activeId)
                        ? "bg-brand-50 text-ink-900 font-semibold border-l-2 border-brand-400 -ml-[2px] pl-[10px]"
                        : "text-warm-700 hover:bg-ink-50 hover:text-ink-900",
                    ].join(" ")}
                  >
                    <span className="text-[10px] font-bold text-warm-500 mr-2 tabular-nums">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    {s.title}
                  </a>
                  {s.subsections.length > 0 && (
                    <div className="ml-8 mt-0.5 space-y-0.5 border-l border-ink-200 pl-3">
                      {s.subsections.map((sub) => (
                        <a
                          key={sub.id}
                          href={`#${sub.id}`}
                          data-testid={`help-toc-link-${sub.id}`}
                          className={[
                            "block py-0.5 text-[12px] transition-crisp",
                            activeId === sub.id
                              ? "text-brand-600 font-semibold"
                              : "text-warm-600 hover:text-ink-900",
                          ].join(" ")}
                        >
                          {sub.title}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </details>
        </aside>

        {/* Content */}
        <article
          className="prose-doc max-w-3xl text-ink-900"
          data-testid="help-content"
        >
          <SectionWillkommen />
          <SectionWorkflow />
          <SectionMath />
          <SectionTransparenz />
          <SectionDatenschutz />
          <SectionFaq />
          <SectionGlossar />
          <SectionSupport />
        </article>
      </div>
    </HelpLayout>
  );
}

// ---------------------------------------------------------------------------
// Hilfs-Komponenten für konsistente Typografie + Hierarchie
// ---------------------------------------------------------------------------

function H1({ id, children }) {
  return (
    <h1
      id={id}
      className="font-cabinet text-4xl sm:text-5xl tracking-tight font-medium mt-2 mb-6 scroll-mt-20 text-ink-900"
      data-testid={`help-h1-${id}`}
    >
      {children}
    </h1>
  );
}

function H2({ id, children, eyebrow }) {
  return (
    <div className="mt-14 mb-5 scroll-mt-20" id={id}>
      {eyebrow && (
        <div className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-2">
          {eyebrow}
        </div>
      )}
      <h2
        className="font-cabinet text-3xl tracking-tight font-medium text-ink-900"
        data-testid={`help-h2-${id}`}
      >
        {children}
      </h2>
    </div>
  );
}

function H3({ id, children }) {
  return (
    <h3
      id={id}
      className="font-cabinet text-xl tracking-tight font-semibold text-ink-900 mt-10 mb-3 scroll-mt-20"
      data-testid={`help-h3-${id}`}
    >
      {children}
    </h3>
  );
}

function P({ children }) {
  return (
    <p className="text-[15px] leading-7 text-ink-800 mb-4">{children}</p>
  );
}

function Lead({ children }) {
  return (
    <p className="text-lg leading-8 text-warm-700 mb-6 max-w-2xl">
      {children}
    </p>
  );
}

function Note({ tone = "info", children, title }) {
  const palette = {
    info: "border-l-4 border-brand-400 bg-brand-50/50 text-ink-800",
    warn: "border-l-4 border-amber-500 bg-amber-50/60 text-ink-800",
    success: "border-l-4 border-emerald-500 bg-emerald-50/50 text-ink-800",
  }[tone];
  return (
    <div className={`my-5 px-4 py-3 rounded-r-sm text-[14px] leading-6 ${palette}`}>
      {title && (
        <div className="text-[11px] font-bold uppercase tracking-wider mb-1 opacity-80">
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

function UL({ children }) {
  return (
    <ul className="list-disc pl-6 mb-4 text-[15px] leading-7 text-ink-800 space-y-1.5 marker:text-brand-400">
      {children}
    </ul>
  );
}

function Strong({ children }) {
  return <strong className="font-semibold text-ink-900">{children}</strong>;
}

function Kbd({ children }) {
  return (
    <span className="inline-block px-1.5 py-0.5 mx-0.5 rounded-sm border border-ink-300 bg-white text-[11px] font-mono text-ink-900 shadow-[0_1px_0_rgba(0,0,0,0.04)] align-baseline">
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 1 — Willkommen
// ---------------------------------------------------------------------------

function SectionWillkommen() {
  return (
    <section>
      <H1 id="willkommen">ET-Dokumentation</H1>
      <Lead>
        ET unterstützt Sachbearbeiter:innen in HR und Personalverrechnung bei
        der kollektivvertraglichen Einstufung neuer Mitarbeiter:innen — vom
        Lesen der Vordienstzeit-Dokumente bis zur ausgedruckten Empfehlung
        mit Lohngruppe und Gehaltsstufe.
      </Lead>
      <P>
        Diese Dokumentation beschreibt, wie ET arbeitet, welche Logiken im
        Hintergrund laufen und wie du das Tool optimal nutzt. Sie ist
        ausdrücklich für die fachliche Anwender:innen-Perspektive geschrieben
        — keine technischen Interna, keine Code-Begriffe.
      </P>
      <P>
        Wenn du neu bist, lies einfach von oben nach unten. Wenn du nach
        einem konkreten Begriff oder einer konkreten Regel suchst, springe
        über das Inhaltsverzeichnis links direkt zur passenden Sektion.
      </P>
      <Note tone="info" title="Tipp">
        Drücke <Kbd>Strg</Kbd>+<Kbd>F</Kbd> (Win) bzw. <Kbd>⌘</Kbd>+<Kbd>F</Kbd>{" "}
        (Mac), um auf dieser Seite nach einem Begriff zu suchen. Die ganze
        Dokumentation steht in einem einzigen langen Dokument — keine
        versteckten Reiter.
      </Note>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 2 — Workflow
// ---------------------------------------------------------------------------

function SectionWorkflow() {
  return (
    <section>
      <H2 id="workflow" eyebrow="Kapitel 02">
        Der 4-Schritt-Workflow
      </H2>
      <P>
        Eine Einstufung läuft in ET immer in derselben Reihenfolge ab. Du
        kannst jederzeit zu einem früheren Schritt zurückspringen und
        Angaben anpassen — ET berechnet die Folgeschritte sofort neu.
      </P>
      <DiagramWorkflow />

      <H3 id="schritt-1">Schritt 1 — KV auswählen & Stichtag setzen</H3>
      <P>
        Im ersten Schritt wählst du den Kollektivvertrag, der für die
        einzustufende Person gelten soll, und legst den Stichtag fest.
      </P>
      <UL>
        <li>
          <Strong>Kollektivvertrag:</Strong> Wähle aus der Karten-Übersicht
          den passenden KV. Jeder KV bringt seine eigenen Lohngruppen,
          Gehaltsstufen, Anrechnungs- und Sonderregeln mit. Die Liste zeigt
          nur freigegebene KVs deiner Organisation.
        </li>
        <li>
          <Strong>Stichtag:</Strong> Das ist der Tag, an dem die einzustufende
          Person ihre Beschäftigung beim aktuellen Arbeitgeber{" "}
          <em>beginnt</em>. Alle Beschäftigungen <em>davor</em> sind
          Vordienstzeiten und werden ab Schritt 3 geprüft.
        </li>
        <li>
          Der Stichtag lässt sich frei wählen oder mit dem{" "}
          <Strong>Heute-Toggle</Strong> auf das aktuelle Datum setzen.
        </li>
      </UL>
      <Note tone="info" title="Warum der Stichtag so wichtig ist">
        Der Stichtag definiert die Trennlinie zwischen „Vordienst“ und
        „aktueller Anstellung“. Eine Beschäftigung, die am Stichtag selbst
        oder danach beginnt, wird NICHT mehr als Vordienst gezählt. Der
        Stichtag dient auch zur Berechnung der Lückenzeiten und zur Prüfung
        von Sonderregeln.
      </Note>

      <H3 id="schritt-2">Schritt 2 — Dokumente hochladen</H3>
      <P>
        Lade die Belegdokumente der einzustufenden Person hoch. ET liest
        sie automatisch aus und schlägt eine Liste von Vordienstzeiten vor,
        die du anschließend in Schritt 3 verifizieren kannst.
      </P>
      <UL>
        <li>
          <Strong>Typische Dokumente:</Strong> Versicherungsdatenauszug der
          ÖGK / SV, Dienstzeugnisse, Lebensläufe, Arbeitsbestätigungen.
        </li>
        <li>
          <Strong>Formate:</Strong> PDF (auch gescannt), JPG, PNG. Auch
          mehrere Dateien in einem Upload sind erlaubt — ET kombiniert die
          Inhalte automatisch.
        </li>
        <li>
          <Strong>Verarbeitung:</Strong> Die Dokumente werden ausschließlich
          im Arbeitsspeicher von ET ausgewertet. Sobald die Auswertung
          abgeschlossen ist, werden sie verworfen. ET speichert KEINE
          Dokumenten-Originale auf der Festplatte. Mehr dazu siehe{" "}
          <a className="underline-offset-2 hover:underline text-brand-600" href="#datenschutz">
            Datenschutz
          </a>
          .
        </li>
      </UL>
      <Note tone="warn" title="Einwilligung zur Datenübermittlung">
        Vor dem ersten Upload bestätigst du per Checkbox, dass die Daten
        zur Auswertung an die ET-Backend-Services übermittelt werden dürfen.
        Ohne diese Einwilligung lässt sich Schritt 2 nicht starten.
      </Note>

      <H3 id="schritt-3">Schritt 3 — Vordienstzeiten prüfen</H3>
      <P>
        Das Herzstück. ET präsentiert dir alle aus den Dokumenten gelesenen
        Beschäftigungszeiten als editierbare Tabelle. Pro Zeile kannst du:
      </P>
      <UL>
        <li>
          <Strong>Datum von / bis</Strong> korrigieren, falls das automatische
          Lesen unsauber war.
        </li>
        <li>
          <Strong>Arbeitgeber</Strong> und <Strong>Tätigkeit</Strong> anpassen.
          Korrekte Arbeitgebernamen sind besonders wichtig für die
          Sonderregel „Selber Arbeitgeber“ (siehe{" "}
          <a className="underline-offset-2 hover:underline text-brand-600" href="#math-bypass">
            Berechnung &amp; Regeln → Selber Arbeitgeber
          </a>
          ).
        </li>
        <li>
          <Strong>Anrechenbar</Strong>-Häkchen setzen oder entfernen — du
          entscheidest pro Position, ob sie in die Einstufung einfließt. ET
          schlägt eine Voreinstellung vor, du kannst sie immer übersteuern.
        </li>
        <li>
          <Strong>Anrechnungsfaktor</Strong> wählen (volle Anrechnung 1.0,
          halbierte 0.5, oder keine 0.0). ET begründet, wie der Vorschlag
          zustande kommt — du behältst die letzte Entscheidung.
        </li>
        <li>
          <Strong>„Selber Arbeitgeber“</Strong>-Checkbox aktivieren, wenn
          die Position beim aktuellen Arbeitgeber stattfand und über die
          KV-Höchstanrechnungsdauer hinaus zählen soll (siehe{" "}
          <a className="underline-offset-2 hover:underline text-brand-600" href="#math-bypass">
            Sonderregel
          </a>
          ).
        </li>
      </UL>
      <P>
        Neben jeder Zeile zeigt ET kompakte Badges („KV-Regel“, „Annahme“,
        „Manuell“), die transparent machen, wie der vorgeschlagene
        Anrechnungsfaktor zustande kam.
      </P>
      <Note tone="info" title="Du bist die letzte Instanz">
        ET schlägt vor — du entscheidest. Bei jeder Änderung an einer Zeile
        rechnet ET die Gesamtsumme und die voraussichtliche Gehaltsstufe
        automatisch neu durch. Es gibt keine „falsche Reihenfolge“ — du
        kannst alles beliebig oft anpassen.
      </Note>

      <H3 id="schritt-4">Schritt 4 — Empfehlung &amp; PDF-Export</H3>
      <P>
        Im letzten Schritt liefert ET die finale Einstufungsempfehlung mit
        Lohngruppe, Gehaltsstufe, anrechenbarer Dienstzeit und einer
        ausformulierten Begründung.
      </P>
      <UL>
        <li>
          <Strong>Lohngruppe</Strong>: ergibt sich aus der Tätigkeit der
          einzustufenden Person und den KV-Lohngruppen-Definitionen.
        </li>
        <li>
          <Strong>Gehaltsstufe</Strong>: ergibt sich deterministisch aus der
          anrechenbaren Dienstzeit und der KV-Stufentabelle. ET berechnet
          die Stufe direkt aus den Monaten — frei von subjektiven
          Auslegungen.
        </li>
        <li>
          <Strong>Begründung</Strong>: ein ausformulierter Text, der die
          Einstufung nachvollziehbar macht (besonders nützlich für die
          Personalakte oder Streitfälle).
        </li>
        <li>
          <Strong>PDF-Export</Strong>: ein Klick erzeugt ein druckbares
          PDF mit allen Daten — inklusive der Vordienst-Liste und den
          relevanten Hinweisen. Auch dieser Schritt läuft ohne Speicherung
          auf der Festplatte ab.
        </li>
      </UL>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 3 — Berechnung & Regeln
// ---------------------------------------------------------------------------

function SectionMath() {
  return (
    <section>
      <H2 id="math" eyebrow="Kapitel 03">
        Berechnung &amp; Regeln verständlich erklärt
      </H2>
      <P>
        Dieser Abschnitt erklärt die rechnerischen Grundlagen, mit denen ET
        arbeitet — bewusst in Worten und Beispielen, ohne Formeln.
      </P>

      <H3 id="math-30360">Die 30/360-Rechenmethode</H3>
      <P>
        ET rechnet Beschäftigungsdauern in der <Strong>kaufmännischen
        30/360-Konvention</Strong>: jedes Monat hat 30 Tage, jedes Jahr
        360 Tage. Das ist branchenüblich im KV-Bereich und vereinfacht
        Vergleichbarkeit und Berechnung.
      </P>
      <Diagram30360 />
      <P>
        Das echte Kalenderjahr hat 365 Tage. In ET zählt es 360. Dadurch
        weichen ET-Resultate immer geringfügig vom echten Kalender ab — das
        ist gewollt und entspricht der KV-Praxis.
      </P>
      <Note tone="info" title="Beispiel">
        Eine Beschäftigung vom 01.01.2020 bis 31.12.2024 = 5 volle Kalender­jahre
        = in der 30/360-Methode <Strong>1800 Tage = 5 × 360</Strong>. Ein
        Item vom 01.06.2024 bis 31.05.2025 = 360 Tage = 1 Jahr.
      </Note>

      <H3 id="math-cap">Höchstanrechnungsdauer</H3>
      <P>
        Jeder KV legt eine <Strong>Höchstanrechnungsdauer</Strong> fest. Das
        ist die maximale Anzahl an Vordienstjahren, die in die Einstufung
        einfließen darf. Im KV Handel sind es zum Beispiel 7 Jahre.
      </P>
      <P>
        Wenn die Summe der anrechenbaren Vordienste die Höchstanrechnungsdauer
        überschreitet, kappt ET die überschüssigen Tage. Diese werden in der
        Tabelle nicht entfernt, sondern als „über Höchstgrenze hinaus“ markiert —
        du siehst sie weiterhin, sie zählen aber nicht für die Stufenwahl.
      </P>

      <H3 id="math-bypass">Sonderregel „Selber Arbeitgeber“</H3>
      <P>
        Viele KVs erlauben eine Ausnahme von der Höchstanrechnungsdauer:
        Beschäftigungszeiten beim <em>aktuellen</em> Arbeitgeber dürfen{" "}
        <Strong>zusätzlich</Strong> zur Höchstanrechnungsdauer angerechnet
        werden. In ET heißt diese Regel schlicht „Selber Arbeitgeber“.
      </P>
      <P>
        Aktiviere die <Kbd>Selber Arbeitgeber</Kbd>-Checkbox in Schritt 3 bei
        jeder Position, die zum aktuellen Arbeitgeber gehört. Die Sonderregel
        erlaubt typischerweise Lücken von bis zu 24 Monaten zwischen den
        Positionen desselben Arbeitgebers und bis zum Stichtag.
      </P>
      <DiagramCapBypassChain />
      <Note tone="warn" title="Wichtige Konsequenz">
        Wenn an einer Stelle der zusammenhängenden Beschäftigung die Lücke
        24 Monate überschreitet, gilt die <Strong>komplette</Strong> Abfolge
        dieses Arbeitgebers als unterbrochen. ET markiert dann ALLE
        betroffenen Positionen mit einem orange Warnhinweis. Du entscheidest
        manuell, ob die Sonderregel trotzdem anwendbar ist (z. B. weil eine
        Karenz oder Krankheit die Lücke erklärt).
      </Note>
      <DiagramCapBreakdown />

      <H3 id="math-overlap">Überlappungen</H3>
      <P>
        Wenn zwei Beschäftigungen zeitlich überlappen (z. B. parallel zwei
        Anstellungen), rechnet ET den überschnittenen Bereich nur EINMAL an
        — nicht doppelt.
      </P>
      <UL>
        <li>
          Die längere bzw. besser dokumentierte Position bleibt unverändert.
        </li>
        <li>
          Bei der zweiten Position wird der überlappende Teil aus der
          Anrechenbaren Dauer abgezogen. Die Roh-Dauer bleibt sichtbar.
        </li>
        <li>
          In der Zeile siehst du dann zwei Werte: die Roh-Dauer und die
          tatsächlich anrechenbare Dauer.
        </li>
      </UL>

      <H3 id="math-faktor">Anrechnungsfaktor</H3>
      <P>
        Manche Positionen werden nur teilweise oder gar nicht angerechnet.
        ET verwendet drei Standardfaktoren:
      </P>
      <UL>
        <li>
          <Strong>1.0 — volle Anrechnung:</Strong> die übliche Voreinstellung
          für reguläre Beschäftigungen.
        </li>
        <li>
          <Strong>0.5 — halbe Anrechnung:</Strong> z. B. für Praktika oder
          bestimmte KV-Sonderregeln (etwa branchenfremde Vordienste).
        </li>
        <li>
          <Strong>0.0 — keine Anrechnung:</Strong> z. B. AMS-Zeiten,
          Karenz, Wehrdienst, schulische Ausbildung ohne berufliche
          Relevanz. ET schlägt das automatisch vor, du kannst es jederzeit
          übersteuern.
        </li>
      </UL>
      <P>
        Welcher Faktor vorgeschlagen wird, hängt vom KV ab. Die kleine
        Badge neben jeder Zeile zeigt dir transparent, woher der Vorschlag
        kommt: <Kbd>KV-Regel</Kbd>, <Kbd>Annahme</Kbd> oder{" "}
        <Kbd>Manuell</Kbd>.
      </P>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 4 — Transparenz
// ---------------------------------------------------------------------------

function SectionTransparenz() {
  return (
    <section>
      <H2 id="transparenz" eyebrow="Kapitel 04">
        Transparenz &amp; Determinismus
      </H2>
      <P>
        ET legt Wert darauf, dass jede Empfehlung nachvollziehbar ist. Drei
        Prinzipien stehen dahinter:
      </P>
      <UL>
        <li>
          <Strong>Nachvollziehbare Berechnung:</Strong> Die Anrechenbare
          Dauer und die daraus folgende Gehaltsstufe werden direkt aus
          deinen Eingaben und der KV-Stufentabelle berechnet — nicht
          geschätzt. Bei gleichem Input kommt immer dasselbe Ergebnis
          heraus.
        </li>
        <li>
          <Strong>Vollständiges Berechnungsprotokoll:</Strong> Klicke in
          Schritt 3 auf „Wie wird berechnet?“ — ET öffnet ein Detail-Modal
          mit der Aufschlüsselung pro Zeile, der Höchstgrenzen-Anwendung
          und der Verbuchung der „Selber Arbeitgeber“-Sonderregel. Nichts
          ist versteckt.
        </li>
        <li>
          <Strong>Regel-Quelle pro Zeile:</Strong> Jeder vorgeschlagene
          Anrechnungsfaktor trägt ein Etikett, das seine Quelle nennt
          (KV-Regel / Annahme / Manuell). Du erkennst auf einen Blick, was
          du übersteuert hast und was direkt aus dem KV-Regelwerk kommt.
        </li>
      </UL>
      <Note tone="success" title="Was bedeutet das in der Praxis?">
        Du kannst jede Empfehlung gegenüber der einzustufenden Person, dem
        Betriebsrat oder im Streitfall begründen — mit konkreten Zahlen
        und mit dem Verweis auf die KV-Regel. ET hilft dir, deine
        Entscheidung zu fundieren, nicht sie zu ersetzen.
      </Note>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 5 — Datenschutz
// ---------------------------------------------------------------------------

function SectionDatenschutz() {
  return (
    <section>
      <H2 id="datenschutz" eyebrow="Kapitel 05">
        Datenschutz
      </H2>
      <Lead>
        ET ist von Grund auf so gebaut, dass <Strong>keine Kundendaten und
        keine hochgeladenen Dokumente dauerhaft auf der Festplatte
        gespeichert</Strong> werden.
      </Lead>
      <UL>
        <li>
          <Strong>Dokumente:</Strong> werden ausschließlich im Arbeitsspeicher
          ausgewertet und sofort nach der Auswertung verworfen. Keine
          Kopien, keine Backups, keine Archive auf Disk.
        </li>
        <li>
          <Strong>Vordienst-Zeilen:</Strong> werden während der Sitzung
          gehalten, damit du sie korrigieren und schrittweise weiterführen
          kannst. Schließt du den Tab oder loggst dich aus, sind sie weg.
        </li>
        <li>
          <Strong>Änderungsprotokoll:</Strong> ET protokolliert anonymisierte
          Metadaten (z. B. „Schritt 3 abgeschlossen, 6 Vordienst-Items
          verarbeitet, KV Handel“) — ohne Namen, ohne Geburtsdaten, ohne
          Dokumentinhalte.
        </li>
        <li>
          <Strong>Einwilligungspflicht:</Strong> Bevor Daten zur Auswertung
          übermittelt werden, bestätigst du dies in Schritt 2 ausdrücklich.
        </li>
      </UL>
      <Note tone="info" title="Standortbezogene Datenverarbeitung">
        Die ET-Auswertungsservices laufen in der Europäischen Union. Eine
        Übertragung in Drittländer findet nicht statt.
      </Note>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 6 — FAQ
// ---------------------------------------------------------------------------

const FAQ_ITEMS = [
  {
    q: 'Warum bekommt eine Vordienst-Zeile bei mir das Häkchen „nicht anrechenbar“?',
    a: "ET prüft automatisch typische Ausschluss-Tätigkeiten (AMS-Bezug, Karenz, Wehrdienst, schulische Ausbildung) und setzt das Häkchen entsprechend. Du kannst jede Zeile manuell übersteuern, indem du das Häkchen wieder aktivierst. Die Begründung neben dem Häkchen verrät dir, warum ET den Vorschlag so gemacht hat.",
  },
  {
    q: "Wie ändere ich den Anrechnungsfaktor einer Zeile?",
    a: 'In der Vordienst-Tabelle (Schritt 3) klickst du in der jeweiligen Zeile auf das Faktor-Feld und wählst 1.0, 0.5 oder 0.0. Sobald du einen Wert manuell setzt, erscheint die Badge „Manuell“ und ET respektiert deine Wahl bis zum Sitzungsende.',
  },
  {
    q: "Was bedeutet das orange Warning-Banner über einer Zeile?",
    a: "Das ist ein Hinweis von ET, dass etwas an dieser Zeile besondere Aufmerksamkeit verdient — etwa eine Lücke zur nächsten Position desselben Arbeitgebers, die länger als 24 Monate ist, oder eine Tätigkeit, deren Einordnung nicht eindeutig ist. ET sperrt nichts, sondern bittet dich nur, kurz draufzuschauen.",
  },
  {
    q: "Im Modal stehen Höchstanrechnungsdauer (z. B. 7 J.) und Gesamt-Anrechenbar (z. B. 14 J. 1 M.) unterschiedlich — was zählt für die Gehaltsstufe?",
    a: 'Für die Gehaltsstufe zählt immer der Endwert „Anrechenbare Dienstzeit gesamt“ in der Aufschlüsselung. Das ist die Summe aus dem Anteil bis zur Höchstgrenze und der Sonderregel „Selber Arbeitgeber“ — und genau diese Zahl verwendet ET nachvollziehbar, um die Stufe aus der KV-Tabelle abzulesen.',
  },
  {
    q: 'Mein Item zeigt „11 M. 21 T.“ und darunter klein „gesamt 11 M. 21 T.“ — sind das zwei verschiedene Werte?',
    a: 'Nein, das wäre ein Anzeigefehler. Aktuelle ET-Versionen zeigen den Untertitel „gesamt …“ nur noch dann, wenn die Anrechenbare Dauer TATSÄCHLICH von der Rohdauer abweicht (z. B. durch Höchstgrenzen-Reduktion, Faktor < 1 oder Überlappung). Sind beide Werte identisch, fällt der Untertitel weg.',
  },
  {
    q: "Mein PDF wurde nicht erstellt — was tun?",
    a: "Stelle sicher, dass du in Schritt 1–3 alle notwendigen Angaben gemacht hast und in Schritt 4 die Empfehlung vollständig geladen wurde (kein Spinner mehr aktiv). Falls der PDF-Button trotzdem nicht reagiert: Browser-Tab neu laden, dann Schritt 4 nochmal aufrufen. Bleibt es bestehen, melde es dem Support — gerne mit Browser-Konsole-Screenshot.",
  },
  {
    q: "ET-Empfehlung weicht von meiner Einschätzung ab — was zählt arbeitsrechtlich?",
    a: "ET liefert eine technisch korrekte Empfehlung auf Basis deiner Eingaben und der KV-Regeln. Die finale rechtliche Verantwortung für die Einstufung liegt aber bei dir bzw. der einstufenden Stelle. ET ist Entscheidungs-Hilfe, kein Rechtsbeistand. Bei Unsicherheit konsultiere den Betriebsrat oder einen Arbeitsrechtsexperten.",
  },
  {
    q: "Ich habe in Schritt 3 etwas geändert — muss ich Schritt 4 manuell neu starten?",
    a: "Nein. ET rechnet bei jeder Änderung in Schritt 3 die Folgewerte automatisch durch. Wenn du Schritt 4 öffnest, siehst du immer die aktuelle Empfehlung basierend auf dem aktuellen Stand von Schritt 3.",
  },
  {
    q: "Kann ich eine Einstufung später nochmal aufrufen?",
    a: "Aus Datenschutzgründen werden Einstufungen nicht dauerhaft gespeichert. Wenn du eine Einstufung dokumentieren möchtest, exportiere sie als PDF — das ist das offizielle Arbeitsergebnis. Den PDF-Inhalt kannst du in deiner Personalakte ablegen.",
  },
  {
    q: "Was ist der Unterschied zwischen Lohngruppe und Gehaltsstufe?",
    a: "Die Lohngruppe ergibt sich aus der TÄTIGKEIT (was die Person tut). Die Gehaltsstufe ergibt sich aus der DIENSTZEIT (wie lange anrechenbar). Beide zusammen ergeben den konkreten KV-Lohn / das Gehalt laut KV-Tabelle.",
  },
];

function SectionFaq() {
  return (
    <section>
      <H2 id="faq" eyebrow="Kapitel 06">
        Häufige Fragen
      </H2>
      <div className="space-y-3">
        {FAQ_ITEMS.map((item, i) => (
          <details
            key={i}
            className="border border-ink-200 rounded-sm bg-white px-4 py-3 transition-crisp open:border-brand-300 open:bg-brand-50/20"
            data-testid={`help-faq-item-${i}`}
          >
            <summary className="cursor-pointer text-[15px] font-semibold text-ink-900 select-none flex items-start gap-3">
              <span className="text-brand-600 font-mono text-sm pt-0.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="flex-1">{item.q}</span>
            </summary>
            <div className="mt-3 ml-7 text-[14px] leading-7 text-ink-800">
              {item.a}
            </div>
          </details>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 7 — Glossar
// ---------------------------------------------------------------------------

const GLOSSAR = [
  ["Anrechenbar", "Eine Vordienst-Position zählt für die Einstufung. Das Häkchen entscheidet das pro Zeile."],
  ["Anrechnungsfaktor", "Multiplikator für die Dauer einer Position (1.0 = voll, 0.5 = halb, 0.25 = ein Viertel, 0.0 = nicht). Bestimmt, wie viel von der Roh-Dauer in die Anrechenbare Dauer einfließt."],
  ["Gehaltsstufe", "Position innerhalb der KV-Lohntabelle, bestimmt durch die anrechenbare Dienstzeit. Bei manchen KVs als Buchstabe (a, b, c, d), bei anderen als Zahl (1, 2, 3, …)."],
  ["Höchstanrechnungsdauer", "Maximalwert an Vordienstjahren, die in die Einstufung einfließen. KV-spezifisch (z. B. 7 Jahre im KV Handel)."],
  ["Kollektivvertrag (KV)", "Branchenspezifischer Tarifvertrag, der Lohngruppen, Stufen und Anrechnungsregeln festlegt. Wählst du in Schritt 1."],
  ["Lohngruppe", "Position innerhalb der KV-Lohntabelle, bestimmt durch die TÄTIGKEIT."],
  ["Regel-Quelle (Badge)", "Kleine Markierung neben einer Zeile, die zeigt, woher der Vorschlag kommt — KV-Regel, Annahme oder Manuell."],
  ["Roh-Dauer", "Die kalendarische 30/360-Dauer einer Beschäftigung, OHNE Anwendung von Höchstgrenze, Faktor oder Überlappungs-Bereinigung."],
  ["Selber Arbeitgeber (Sonderregel)", 'KV-Sonderregel: Beschäftigungszeiten beim aktuellen Arbeitgeber dürfen zusätzlich zur Höchstanrechnungsdauer angerechnet werden. Voraussetzung: keine Lücke länger als 24 Monate zwischen den Positionen.'],
  ["Stichtag", "Trennlinie zwischen Vordienst und aktuellem Arbeitsverhältnis. Beginn des neuen Arbeitsverhältnisses."],
  ["Vordienstzeit", "Jede Beschäftigung VOR dem Stichtag. Wird in Schritt 3 als Tabelle verarbeitet."],
  ["30/360-Methode", "Kaufmännische Rechenkonvention: jedes Monat 30 Tage, jedes Jahr 360 Tage. Branchenstandard im KV-Bereich."],
];

function SectionGlossar() {
  return (
    <section>
      <H2 id="glossar" eyebrow="Kapitel 07">
        Glossar
      </H2>
      <dl className="grid sm:grid-cols-[180px_minmax(0,1fr)] gap-x-6 gap-y-4 mt-4">
        {GLOSSAR.map(([term, def]) => (
          <React.Fragment key={term}>
            <dt className="font-semibold text-ink-900 text-[14px] tabular-nums">
              {term}
            </dt>
            <dd className="text-[14px] leading-7 text-warm-700">{def}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SEKTION 8 — Support & Feedback
// ---------------------------------------------------------------------------

function SectionSupport() {
  return (
    <section>
      <H2 id="support" eyebrow="Kapitel 08">
        Support &amp; Feedback
      </H2>
      <P>
        Diese Dokumentation deckt die häufigsten Anwendungsfälle ab. Wenn
        du auf etwas stößt, das hier nicht steht, oder einen Fehler findest:
        sag Bescheid.
      </P>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div className="border border-ink-200 rounded-sm bg-white p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-1">
            Bug melden
          </div>
          <div className="font-semibold text-ink-900 mb-2">
            Etwas funktioniert nicht wie erwartet?
          </div>
          <P>
            Schreib uns mit einer kurzen Beschreibung, was du erwartet
            hast und was passiert ist. Ein Screenshot hilft enorm. Im
            besten Fall mit den genauen Schritten zum Nachstellen.
          </P>
        </div>
        <div className="border border-ink-200 rounded-sm bg-white p-5">
          <div className="text-[10px] font-bold uppercase tracking-wider text-brand-600 mb-1">
            Feature wünschen
          </div>
          <div className="font-semibold text-ink-900 mb-2">
            Du hast eine Idee für eine Verbesserung?
          </div>
          <P>
            Beschreib uns kurz, welche Aufgabe du erleichtern möchtest und
            warum. Die meisten ET-Features sind aus genau solchen
            Vorschlägen entstanden.
          </P>
        </div>
      </div>
      <Note tone="info" title="Kontakt">
        Wende dich an deine Organisations-Ansprechperson für ET oder
        direkt an den Vorlagenportal-Support. Die Kontaktdaten findest du
        im Impressum.
      </Note>
    </section>
  );
}
